import { useEffect, useRef } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';

import type {
  DTOActiveSubscription,
  DTOSubscriptionBenefitItem,
} from 'src/apis/dto/DTOSubscription';
import { fetchSubscriptionProductsBenefit } from 'src/apis/service/SubscriptionService';
import { getInventoryAtom } from 'src/stores/auth/atoms';
import { client } from 'src/utils/api';

import { SUBSCRIPTION_QUERY_KEYS } from './useActiveSubscriptions';

const POLLING_INTERVAL_MS = 1000;
const MAX_POLLING_TIME_MS = 30000;

/**
 * 결제 성공 후 구독 상태 확인 후 성공 처리
 * https://www.notion.so/hpcnt/Tech-Spec-Azar-V3-Stripe-Web-Subscription-320ce00cd3548079a187d41c02541468?source=copy_link#3909f646cbd04315a5af44e4930c747e
 */
export const useSubscriptionCheckoutSuccess = (
  subscriptionId: string | null,
  onSuccess?: (benefit: DTOSubscriptionBenefitItem[]) => void,
  onError?: () => void
) => {
  const queryClient = useQueryClient();
  const getInventory = useSetAtom(getInventoryAtom);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const hasSucceededRef = useRef(false);

  useEffect(() => {
    if (!subscriptionId) return;

    hasSucceededRef.current = false;
    const startTime = Date.now();

    const clearPending = () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };

    const handleFailure = () => {
      clearPending();
      onErrorRef.current?.();
    };

    const pollSubscriptionStatus = async () => {
      try {
        const response = await client.get<DTOActiveSubscription>(
          `/api/v1/subscriptions/${subscriptionId}`
        );

        if (response.data.state === 'ACTIVATED') {
          if (hasSucceededRef.current) return;
          hasSucceededRef.current = true;

          clearPending();
          queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_QUERY_KEYS.active() });
          getInventory();

          let benefits: DTOSubscriptionBenefitItem[] = [];
          try {
            const benefitResponse = await fetchSubscriptionProductsBenefit(response.data.productId);
            benefits = benefitResponse.items;
          } catch {
            // benefit 조회 실패해도 성공 처리는 진행하지만 benefit은 null로 반환
          }

          onSuccessRef.current?.(benefits);
          return;
        }

        if (response.data.state === 'INACTIVATED') {
          handleFailure();
          return;
        }

        if (Date.now() - startTime > MAX_POLLING_TIME_MS) {
          handleFailure();
        }
      } catch {
        if (Date.now() - startTime > MAX_POLLING_TIME_MS) {
          handleFailure();
        }
      }
    };

    pollSubscriptionStatus();
    pollingRef.current = setInterval(pollSubscriptionStatus, POLLING_INTERVAL_MS);

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [subscriptionId, queryClient, getInventory]);
};
